import { useCallback } from 'react';

export type EvaluationPayload<TData, TQuery> = {
  data: TData;
  query: TQuery;
};

export type EvaluationSignature<TData, TQuery> = (
  data: TData,
  query: TQuery
) => number | false;

/**
 * Wrapper util that returns a refetch function to be used by the query to determine its refetch interval,
 * or cancel the refetch. [Docs here](https://react-query.tanstack.com/reference/useQuery#:~:text=refetchInterval%3A%20number%20%7C%20false,compute%20a%20frequency)
 * @param refetchInterval The number of milliseconds between calls if shouldRefetch is true for the last query
 * @param shouldRefetch Determines whether or not the query should refetch data
 */
export const useDetermineQueryRefetchInterval = <TData, TQuery>(
  refetchInterval: number,
  shouldRefetch: (payload: EvaluationPayload<TData, TQuery>) => boolean
): EvaluationSignature<TData, TQuery> =>
  useCallback(
    (data: TData, query: TQuery): number | false => {
      const payload = { data, query };
      if (shouldRefetch(payload)) {
        return refetchInterval;
      }

      return false;
    },
    [refetchInterval, shouldRefetch]
  );
